import { useState, useEffect, useCallback, useMemo } from 'react';
import { useDeviceStatus } from '../context/DeviceStatusContext';
import { getAllFirmwareVersions, triggerOTAUpdate, triggerBulkOTAUpdate } from '../utils/OTAService';
import { WS_MESSAGE_TYPES } from '../constants/config';
import {
  Cpu, RefreshCw, UploadCloud, CheckCircle, XCircle, Loader2,
  Package, AlertTriangle, Wifi, WifiOff, Filter, ChevronUp,
  Zap, ShieldCheck, Info, HardDrive, Clock, ArrowUpCircle
} from 'lucide-react';
import './Firmware.css';

/* ── helpers ────────────────────────────────────────────── */

const norm = (v) => {
  if (!v || v === 'Desconocida') return null;
  return v.toString().trim().toLowerCase().replace(/^v\.?/i, '').replace(/\s+/g, '');
};

const hasUpdate = (device, latest) => {
  const cur = norm(device.currentFirmwareVersion || device.firmwareVersion);
  const lat = norm(device.model && latest[device.model] ? latest[device.model].version : null);
  return !!(cur && lat && cur !== lat);
};

/* ── toast hook ─────────────────────────────────────────── */

function useToasts() {
  const [list, setList] = useState([]);
  const add = useCallback((message, type = 'info') => {
    const id = Date.now() + Math.random();
    setList(prev => [...prev, { id, message, type }]);
    setTimeout(() => setList(prev => prev.filter(t => t.id !== id)), 4500);
  }, []);
  return { list, add };
}

/* ── Toasts ─────────────────────────────────────────────── */

function Toasts({ items }) {
  const icon = { success: <CheckCircle size={14} />, error: <XCircle size={14} />, info: <Info size={14} />, warning: <AlertTriangle size={14} /> };
  if (!items.length) return null;
  return (
    <div className="fw-toasts">
      {items.map(t => (
        <div key={t.id} className={`fw-toast fw-toast--${t.type}`}>{icon[t.type]}<span>{t.message}</span></div>
      ))}
    </div>
  );
}

/* ── Confirm Modal ──────────────────────────────────────── */

function ConfirmModal({ data, onClose }) {
  if (!data) return null;
  return (
    <div className="fw-backdrop" onClick={onClose}>
      <div className="fw-modal" onClick={e => e.stopPropagation()}>
        <div className="fw-modal__icon"><UploadCloud size={28} /></div>
        <h3>Confirmar actualización</h3>
        <p className="fw-modal__msg">{data.message}</p>
        <div className="fw-modal__actions">
          <button className="fw-btn fw-btn--ghost" onClick={onClose}>Cancelar</button>
          <button className="fw-btn fw-btn--primary" onClick={() => { data.onConfirm(); onClose(); }}>
            <Zap size={15} /> Actualizar
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Version Card ───────────────────────────────────────── */

function VersionCard({ model, info, count }) {
  return (
    <div className="fw-ver-card">
      <div className="fw-ver-card__head">
        <span className="fw-ver-card__model"><Cpu size={13} /> {model}</span>
        {count > 0 && <span className="fw-ver-card__count"><HardDrive size={11} /> {count}</span>}
      </div>
      <div className="fw-ver-card__tag"><ChevronUp size={14} /> {info.version}</div>
      <div className="fw-ver-card__name">{info.name}</div>
      <div className="fw-ver-card__date">
        <Clock size={11} />
        {new Date(info.publishedAt).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })}
      </div>
    </div>
  );
}

/* ── Device Row ─────────────────────────────────────────── */

function DeviceRow({ device, selected, toggle, onOTA, status, latestVersions }) {
  const curVer = device.currentFirmwareVersion || device.firmwareVersion || 'Desconocida';
  const upd = hasUpdate(device, latestVersions);
  const updating = status === 'updating';

  return (
    <tr className={selected ? 'fw-row--selected' : ''}>
      <td>
        <label className="fw-check">
          <input type="checkbox" checked={selected} onChange={toggle} />
          <span className="fw-check__box" />
        </label>
      </td>
      <td className="fw-mac"><code>{device.mac}</code></td>
      <td>
        <div className="fw-device-name">
          {device.name || '-'}
          {device.isActive && <span className="fw-dot fw-dot--on" />}
        </div>
      </td>
      <td><span className="fw-model-tag">{device.model || '-'}</span></td>
      <td>
        <div className="fw-ver-cell">
          <span className="fw-ver-badge">{curVer}</span>
          {upd && (
            <span className="fw-ver-update">
              <ArrowUpCircle size={11} /> {latestVersions[device.model].version}
            </span>
          )}
        </div>
      </td>
      <td className="fw-date">{device.lastUpdate ? new Date(device.lastUpdate).toLocaleString('es-AR') : '-'}</td>
      <td>
        <div className="fw-user">
          <span className="fw-user__name">{device.userName || '-'}</span>
          <span className="fw-user__email">{device.userEmail || ''}</span>
        </div>
      </td>
      <td>
        <div className="fw-status-cell">
          {device.isActive
            ? <span className="fw-conn fw-conn--on"><Wifi size={11} /> Online</span>
            : <span className="fw-conn fw-conn--off"><WifiOff size={11} /> Offline</span>
          }
          {status === 'success' && <span className="fw-ota-badge fw-ota-badge--ok"><CheckCircle size={11} /> OK</span>}
          {status === 'error' && <span className="fw-ota-badge fw-ota-badge--err"><XCircle size={11} /> Error</span>}
          {updating && <span className="fw-ota-badge fw-ota-badge--run"><Loader2 size={11} className="fw-spin" /> OTA...</span>}
        </div>
      </td>
      <td>
        <button className="fw-btn fw-btn--sm" onClick={onOTA} disabled={updating}>
          {updating ? <Loader2 size={14} className="fw-spin" /> : <UploadCloud size={14} />}
          <span className="fw-btn-label">{updating ? 'Enviando' : 'Actualizar'}</span>
        </button>
      </td>
    </tr>
  );
}

/* ── Main ───────────────────────────────────────────────── */

function Firmware() {
  const {
    getAllDevicesWithStatus,
    fetchAllDevices,
    onDeviceEvent
  } = useDeviceStatus();

  const toast = useToasts();

  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [results, setResults] = useState({});
  const [latestVersions, setLatestVersions] = useState({});
  const [bulk, setBulk] = useState({ cur: 0, total: 0, active: false });
  const [outdatedOnly, setOutdatedOnly] = useState(false);
  const [modal, setModal] = useState(null);
  const [spinning, setSpinning] = useState(false);

  /* load */
  useEffect(() => {
    setLoading(true);
    fetchAllDevices()
      .then((fetchedDevices) => {
        const all = (fetchedDevices || []).map(d => ({ ...d, isActive: d.isActive ?? d.isOnline ?? false }));
        const seen = new Set();
        setDevices(all.filter(d => {
          if (!d.mac || seen.has(d.mac)) return false;
          seen.add(d.mac);
          return true;
        }));
        setError(null);
      })
      .catch(err => setError(err?.message || 'Error cargando dispositivos'))
      .finally(() => setLoading(false));
    fetchVersions();
  }, []);

  const sync = () => {
    const all = getAllDevicesWithStatus().map(d => ({ ...d, isActive: d.isActive ?? d.isOnline ?? false }));
    const seen = new Set();
    setDevices(all.filter(d => {
      if (!d.mac || seen.has(d.mac)) return false;
      seen.add(d.mac);
      return true;
    }));
  };

  /* websocket */
  useEffect(() => {
    if (!onDeviceEvent) return;
    const u1 = onDeviceEvent(WS_MESSAGE_TYPES.OTA_RESPONSE, ({ mac, data: d }) => {
      if (!mac) return;
      if (d?.status === 'starting_update') setResults(p => ({ ...p, [mac]: 'updating' }));
      else if (d?.status === 'error' || d?.error) { setResults(p => ({ ...p, [mac]: 'error' })); toast.add(`Error OTA: ${mac}`, 'error'); }
    });
    const u2 = onDeviceEvent(WS_MESSAGE_TYPES.STATUS_RESPONSE, ({ mac, data: d }) => {
      if (!mac) return;
      const fw = d?.firmware_version || d?.data?.device?.firmware_version;
      if (fw) {
        setDevices(p => p.map(x => x.mac === mac ? { ...x, firmwareVersion: fw, status: { ...x.status, firmware_version: fw } } : x));
        toast.add(`${mac} -> ${fw}`, 'success');
      }
    });
    return () => { u1(); u2(); };
  }, [onDeviceEvent]);

  /* versions */
  const fetchVersions = async () => {
    try {
      const v = await getAllFirmwareVersions();
      const f = {};
      Object.entries(v).forEach(([m, info]) => {
        if (info.error || !info.version) return; // Skip models without firmware in release
        f[m] = { version: info.version, publishedAt: info.releaseDate, name: info.name };
      });
      setLatestVersions(f);
    } catch (e) { console.error('Error versiones:', e); }
  };

  /* OTA individual */
  const triggerOTA = async (mac) => {
    try {
      setResults(p => ({ ...p, [mac]: 'updating' }));
      const r = await triggerOTAUpdate(mac);
      if (r.success) {
        setResults(p => ({ ...p, [mac]: 'success' }));
        toast.add(`OTA enviado a ${mac}`, 'success');
        setTimeout(() => setResults(p => { const n = { ...p }; delete n[mac]; return n; }), 5000);
      } else {
        setResults(p => ({ ...p, [mac]: 'error' }));
        toast.add(`Error: ${r.message}`, 'error');
      }
    } catch {
      setResults(p => ({ ...p, [mac]: 'error' }));
      toast.add(`Error de conexión: ${mac}`, 'error');
    }
  };

  /* selection */
  const toggle = (mac) => setSelected(p => { const n = new Set(p); n.has(mac) ? n.delete(mac) : n.add(mac); return n; });
  const selectAll = (list) => {
    const all = list.every(d => selected.has(d.mac));
    setSelected(p => { const n = new Set(p); list.forEach(d => all ? n.delete(d.mac) : n.add(d.mac)); return n; });
  };

  /* bulk OTA */
  const triggerBulk = async () => {
    const macs = Array.from(selected);
    try {
      const r = await triggerBulkOTAUpdate(macs, 2000, (c, t) => setBulk({ cur: c, total: t, active: true }));
      const nr = {};
      r.details.forEach(({ mac, success }) => { nr[mac] = success ? 'success' : 'error'; });
      setResults(nr);
      toast.add(`Bulk OTA: ${r.successful} OK, ${r.failed} fallidas`, r.failed ? 'warning' : 'success');
    } catch (e) {
      toast.add(`Error bulk: ${e.message}`, 'error');
    } finally {
      setBulk({ cur: 0, total: 0, active: false });
      setSelected(new Set());
    }
  };

  /* refresh */
  const handleRefresh = async () => {
    setSpinning(true);
    await fetchAllDevices();
    sync();
    await fetchVersions();
    setTimeout(() => setSpinning(false), 600);
  };

  /* derived */
  const filtered = useMemo(() => {
    if (!outdatedOnly) return devices;
    return devices.filter(d => hasUpdate(d, latestVersions));
  }, [devices, outdatedOnly, latestVersions]);

  const outdatedCount = useMemo(() => devices.filter(d => hasUpdate(d, latestVersions)).length, [devices, latestVersions]);
  const updatingCount = Object.values(results).filter(s => s === 'updating').length;
  const allSelected = filtered.length > 0 && filtered.every(d => selected.has(d.mac));

  const modelCounts = useMemo(() => {
    const c = {};
    devices.forEach(d => { if (d.model) c[d.model] = (c[d.model] || 0) + 1; });
    return c;
  }, [devices]);

  /* ── loading / error ──────────────────────────────────── */

  if (loading) return (
    <div className="fw-page">
      <div className="fw-empty">
        <Loader2 size={36} className="fw-spin" />
        <p>Cargando dispositivos...</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="fw-page">
      <div className="fw-empty fw-empty--err">
        <AlertTriangle size={36} />
        <p>{error}</p>
        <button className="fw-btn fw-btn--primary" onClick={sync}><RefreshCw size={14} /> Reintentar</button>
      </div>
    </div>
  );

  /* ── render ───────────────────────────────────────────── */

  return (
    <div className="fw-page">
      <Toasts items={toast.list} />
      <ConfirmModal data={modal} onClose={() => setModal(null)} />

      {/* Header */}
      <header className="fw-header">
        <div className="fw-header__left">
          <div className="fw-header__icon"><Cpu size={22} /></div>
          <div>
            <h1 className="fw-header__title">Firmware OTA</h1>
            <p className="fw-header__sub">Gestion de actualizaciones over-the-air</p>
          </div>
        </div>
        <button className={`fw-btn fw-btn--ghost${spinning ? ' fw-spinning' : ''}`} onClick={handleRefresh}>
          <RefreshCw size={16} /> Actualizar
        </button>
      </header>

      {/* KPI strip */}
      <div className="fw-kpi-strip">
        <div className="fw-kpi">
          <div className="fw-kpi__icon fw-kpi__icon--devices"><HardDrive size={18} /></div>
          <div className="fw-kpi__body">
            <span className="fw-kpi__val">{devices.length}</span>
            <span className="fw-kpi__label">Dispositivos</span>
          </div>
        </div>
        <div className="fw-kpi">
          <div className="fw-kpi__icon fw-kpi__icon--selected"><CheckCircle size={18} /></div>
          <div className="fw-kpi__body">
            <span className="fw-kpi__val">{selected.size}</span>
            <span className="fw-kpi__label">Seleccionados</span>
          </div>
        </div>
        <div className="fw-kpi fw-kpi--click" onClick={() => setOutdatedOnly(v => !v)}>
          <div className={`fw-kpi__icon fw-kpi__icon--outdated${outdatedOnly ? ' fw-kpi__icon--active' : ''}`}><AlertTriangle size={18} /></div>
          <div className="fw-kpi__body">
            <span className="fw-kpi__val">{outdatedCount}</span>
            <span className="fw-kpi__label">Desactualizados{outdatedOnly ? ' (filtro)' : ''}</span>
          </div>
        </div>
        <div className="fw-kpi">
          <div className={`fw-kpi__icon fw-kpi__icon--updating${updatingCount ? ' fw-kpi__icon--pulse' : ''}`}><Loader2 size={18} /></div>
          <div className="fw-kpi__body">
            <span className="fw-kpi__val">{updatingCount}</span>
            <span className="fw-kpi__label">En progreso</span>
          </div>
        </div>
      </div>

      {/* Latest Versions */}
      {Object.keys(latestVersions).length > 0 && (
        <section className="fw-section">
          <h2 className="fw-section__title"><Package size={16} /> Versiones disponibles</h2>
          <div className="fw-ver-grid">
            {Object.entries(latestVersions).map(([m, info]) => (
              <VersionCard key={m} model={m} info={info} count={modelCounts[m] || 0} />
            ))}
          </div>
        </section>
      )}

      {/* Info banner */}
      <div className="fw-info-banner">
        <ShieldCheck size={16} />
        <span>El servidor construye automaticamente la URL del firmware segun el modelo y perfil de hardware de cada dispositivo.</span>
      </div>

      {/* Toolbar */}
      <div className="fw-toolbar">
        <div className="fw-toolbar__left">
          <button className={`fw-btn fw-btn--ghost fw-btn--sm${allSelected ? ' fw-btn--active' : ''}`} onClick={() => selectAll(filtered)} disabled={bulk.active}>
            {allSelected ? <CheckCircle size={14} /> : <span className="fw-check-outline" />}
            {allSelected ? 'Deseleccionar' : 'Seleccionar todo'}
          </button>
          <button className={`fw-btn fw-btn--ghost fw-btn--sm${outdatedOnly ? ' fw-btn--warning' : ''}`} onClick={() => setOutdatedOnly(v => !v)}>
            <Filter size={14} /> {outdatedOnly ? 'Mostrando desactualizados' : 'Solo desactualizados'}
          </button>
        </div>
        <div className="fw-toolbar__right">
          {selected.size > 0 && (
            <button
              className="fw-btn fw-btn--primary"
              disabled={bulk.active}
              onClick={() => setModal({
                message: `Se actualizaran ${selected.size} dispositivo(s) en cola con 2s de espera entre cada uno.`,
                onConfirm: triggerBulk
              })}
            >
              {bulk.active
                ? <><Loader2 size={14} className="fw-spin" /> {bulk.cur}/{bulk.total}</>
                : <><UploadCloud size={14} /> Actualizar {selected.size}</>
              }
            </button>
          )}
        </div>
      </div>

      {/* Progress bar */}
      {bulk.active && (
        <div className="fw-progress">
          <div className="fw-progress__bar">
            <div className="fw-progress__fill" style={{ width: `${(bulk.cur / bulk.total) * 100}%` }} />
          </div>
          <span className="fw-progress__label">Procesando {bulk.cur} de {bulk.total} — 2s entre cada dispositivo</span>
        </div>
      )}

      {/* Table */}
      <div className="fw-table-wrap">
        <table className="fw-table">
          <thead>
            <tr>
              <th style={{ width: 40 }}>
                <label className="fw-check">
                  <input type="checkbox" checked={allSelected} onChange={() => selectAll(filtered)} />
                  <span className="fw-check__box" />
                </label>
              </th>
              <th>MAC</th>
              <th>Nombre</th>
              <th>Modelo</th>
              <th>Firmware</th>
              <th>Ultima act.</th>
              <th>Usuario</th>
              <th>Estado</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(d => (
              <DeviceRow
                key={d.mac}
                device={d}
                selected={selected.has(d.mac)}
                toggle={() => toggle(d.mac)}
                onOTA={() => triggerOTA(d.mac)}
                status={results[d.mac]}
                latestVersions={latestVersions}
              />
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div className="fw-empty-table">
            {outdatedOnly
              ? <><ShieldCheck size={28} /> <span>Todos los dispositivos estan al dia</span></>
              : <><HardDrive size={28} /> <span>No hay dispositivos registrados</span></>
            }
          </div>
        )}
      </div>
    </div>
  );
}

export default Firmware;
